import { useEffect, useRef, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import api from '@/lib/api';

/**
 * Wraps Cloudflare Turnstile. Fetches the site-key from the backend once, then
 * renders the widget. Calls onVerify(token) whenever a fresh token is issued
 * and onVerify('') when the token expires or errors out.
 *
 * If Turnstile is not enabled server-side (dev/preview), renders nothing and
 * immediately reports verified with a placeholder token so forms remain submittable.
 */
export default function TurnstileWidget({ onVerify, className = '' }) {
  const [cfg, setCfg] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    let mounted = true;
    api.get('/auth/config')
      .then(({ data }) => { if (mounted) setCfg(data); })
      .catch(() => { if (mounted) setCfg({ turnstile_enabled: false }); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (cfg && !cfg.turnstile_enabled) {
      // Not enforced server-side → let the form submit through.
      onVerify?.('dev-bypass');
    }
  }, [cfg, onVerify]);

  if (!cfg) return null; // still loading config
  if (!cfg.turnstile_enabled || !cfg.turnstile_site_key) return null;

  return (
    <div className={className} data-testid="turnstile-widget">
      <Turnstile
        ref={ref}
        siteKey={cfg.turnstile_site_key}
        options={{ theme: 'light', size: 'flexible' }}
        onSuccess={(t) => onVerify?.(t)}
        onError={() => onVerify?.('')}
        onExpire={() => onVerify?.('')}
      />
    </div>
  );
}
